import React, { useState, useEffect, useMemo } from "react";
import { Button, Tooltip, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedClient } from "../../../Reducer/SelectedClientReducer";
import { ProcessService } from "../../../Services/ProcessService/ProcessService";
import Loader from "../../../Components/Loader/Loader";
import InvoiceUploadModal from "./InvoiceUploadModal/InvoiceUploadModal";
import CommonTable from "../../../Components/CustomTable/CustomTable";
import { FileTextOutlined } from "@ant-design/icons";
import Pagination from "../../../Components/CustomPagination/Pagination/Pagination";
import "./Processing.css";
import SelectedClientCard from "../../../Components/SelectedClientCard/SelectedClientCard";
import {
  connection,
  startConnection,
} from "../../../Services/SignalRService/signalrService";

interface RowData {
  processingId: number;
  stageId: number;
  uploadDate: string;
  size: number;
  pages: number;
  type: string;
  device: string;
  uploadedDate: string;
  id: string | number;
}

interface Column {
  key: string;
  title: string;
  isLink?: boolean;
  isStatus?: boolean;
  isUrl?: boolean;
  isError?: boolean;
  isCheckbox?: boolean;
  isDate?: boolean;
  useTag?: boolean;
}

const Processing: React.FC = () => {
  const dispatch = useDispatch();
  const currentUser = localStorage.getItem("user");
  const parsedUser = currentUser ? JSON.parse(currentUser) : null;
  const selectedClient = useSelector((state: any) => state.selectedClient);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<RowData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>(
    [],
  );
  const [showModal, setShowModal] = useState(false);
  const [disableInvoiceButton, setDisableInvoiceButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof RowData;
    direction: "asc" | "desc";
  } | null>(null);

  useEffect(() => {
    if (parsedUser?.roleId === 1) {
      dispatch(setSelectedClient(parsedUser));
    }
  }, [parsedUser?.roleId, dispatch]);

  useEffect(() => {
    if (selectedClient.selectedClient?.userId) {
      fetchData();
    } else {
      setDataSource([]);
      setSelectedRowKeys([]);
    }
  }, [selectedClient.selectedClient?.userId]);

  const fetchData = async () => {
    const encryptedId = localStorage.getItem("encryptedId");
    console.log("Encrypted ID:", encryptedId);
    if (!selectedClient.selectedClient?.userId || !encryptedId) return;
    setLoading(true);
    try {
      const result = await ProcessService(
        selectedClient.selectedClient.userId,
        encryptedId,
      );
      if (result) {
        // Ensure every record has an 'id' field by mapping processingId to id
        const formattedData = result.map((item: any) => ({
          ...item,
          id: item.id || item.processingId, // Fallback to processingId if id is missing
        }));
        setDataSource(formattedData);
        setSelectedRowKeys([]);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Sort dataSource based on sortConfig
  const sortedData = useMemo(() => {
    if (!sortConfig) return dataSource;
    const sorted = [...dataSource].sort((a, b) => {
      const { key, direction } = sortConfig;
      const valA = a[key];
      const valB = b[key];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "number" && typeof valB === "number") {
        return direction === "asc" ? valA - valB : valB - valA;
      }
      return direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
    return sorted;
  }, [dataSource, sortConfig]);

  // Paginate the sorted data
  // Use all sortedData instead of slicing
  const currentRecords = useMemo(() => {
    return sortedData; // no slice
  }, [sortedData]);

  const handleAddInvoice = () => {
    setShowModal(true);
    localStorage.removeItem("buttonStatus");
  };
  // Handle header checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRowKeys([]);
    } else {
      // Only map the IDs of the records currently visible/available
      const allIds = currentRecords
        .map((row) => row.id)
        .filter((id) => id !== undefined);
      setSelectedRowKeys(allIds);
    }
  };

  // Sync selectAll whenever selectedRowKeys or dataSource changes
  useEffect(() => {
    setSelectAll(
      currentRecords.length > 0 &&
        selectedRowKeys.length === currentRecords.length,
    );
  }, [selectedRowKeys, currentRecords]);

  console.log("selected rows :", selectedRowKeys.length, dataSource.length);
  // Columns config for CommonTable
  const columns = [
    {
      key: "selected",
      title: (
        <input
          type="checkbox"
          checked={selectAll}
          onClick={(e) => e.stopPropagation()}
          onChange={handleSelectAll}
        />
      ),
      dataIndex: "",
      isCheckbox: true,
      showSortArrows: false,
    },
    { key: "processingId", title: "Processing ID", showSortArrows: false },
    {
      key: "stageId",
      title: "Processing Status",
      isStatus: true,
      useTag: true,
      showSortArrows: false,
    },
    {
      key: "uploadedDate",
      title: "Uploaded Date",
      isDate: true,
      showSortArrows: false,
    },
    { key: "size", title: "Size (MB)", showSortArrows: false },
    { key: "pages", title: "Pages", showSortArrows: false },
    { key: "type", title: "Type", showSortArrows: false },
    { key: "device", title: "Device", showSortArrows: false },
  ];

  // Handle individual row checkbox
  const handleCheckboxChange = (row: RowData, checked: boolean) => {
    setSelectedRowKeys((prev) => {
      if (checked) {
        // add only if not already included
        return prev.includes(row.id) ? prev : [...prev, row.id];
      } else {
        // remove the unchecked row
        return prev.filter((id) => id !== row.id);
      }
    });
  };

  const waitForConnection = async () => {
    if (connection.state === "Connected") return;

    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (connection.state === "Connected") {
          clearInterval(interval);
          resolve();
        }
      }, 200);
    });
  };

  //signalR connection logs
  useEffect(() => {
    const setupSignalR = async () => {
      try {
        await startConnection();

        // ✅ WAIT UNTIL REALLY CONNECTED
        await waitForConnection();

        console.log("✅ SignalR Connected:", connection.state);

        const userId = selectedClient?.selectedClient?.userId;

        if (userId) {
          await connection.invoke("JoinProcessingGroup", userId);
          console.log("👥 Joined group:", userId);
        }

        // جلوگیری از duplicate listeners
        connection.off("ProcessingUpdated");

        connection.on("ProcessingUpdated", (update) => {
          console.log("🔥 Realtime update:", update);

          setDataSource((prev: any[]) =>
            prev.map((item) =>
              item.processingId === update.processingId
                ? { ...item, ...update }
                : item,
            ),
          );
        });
      } catch (err) {
        console.error("❌ SignalR setup error:", err);
      }
    };

    setupSignalR();

    return () => {
      connection.off("ProcessingUpdated");
    };
  }, [selectedClient?.selectedClient?.userId]);

  // Format values in CommonTable - you can enhance this by passing renderers or add logic here
  // For simplicity, you can transform your data before passing or add flags on Column for special cells.
  console.log("--- Pass Selected Client ---");
  console.log(selectedClient);

  // --- Guards ---

  if (
    selectedClient?.selectedClient?.status &&
    selectedClient?.selectedClient?.status === 0
  ) {
    return <h1>Client verification is required before uploading.</h1>;
  }

  if (!selectedClient?.selectedClient?.userId) {
    return (
      <h1>Please select a client to view their invoices getting processed.</h1>
    );
  }

  if (loading && dataSource.length === 0) {
    return <Loader />;
  }
  return (
    <div className="processing-container">
      <div className="selected-client-toolbar">
        <SelectedClientCard />

        <div className="selected-client-toolbar__actions">
          <Button
            style={{ backgroundColor: "var(--color-brand)", height: "32px" }}
            type="primary"
            onClick={handleAddInvoice}
            disabled={disableInvoiceButton || selectedRowKeys.length !== 0}
          >
            <FileTextOutlined />
            Add Documents
            {/* {disableInvoiceButton ? "Occupied" : "Add Invoice"} */}
          </Button>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <CommonTable
          columns={columns}
          data={currentRecords}
          selectedRowKeys={selectedRowKeys}
          onRowCheckboxChange={handleCheckboxChange}
        />
      )}

      <InvoiceUploadModal
        SelectedClientID={selectedClient.selectedClient?.userId}
        visible={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchData}
        setDisableInvoiceButton={setDisableInvoiceButton}
      />
    </div>
  );
};

export default Processing;
